/**
 * WorkProgram — production planning view.
 * - Pick projects+syncs (re-uses MultiProjectSelector)
 * - Choose period (presets + custom)
 * - Group by: day, week, responsible, location, wbs, phase
 * - Column chooser + filters + export (CSV/PDF/PNG)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import Papa from "papaparse";
import html2canvas from "html2canvas";
import { Calendar, Layers, Sliders, Download, Save } from "lucide-react";
import { http } from "../lib/api";
import MultiProjectSelector from "../components/MultiProjectSelector";
import ColumnsPanel from "../components/ColumnsPanel";
import TaskDetailModal from "../components/TaskDetailModal";
import SavedViewsMenu from "../components/SavedViewsMenu";

const PERIOD_PRESETS = [
  { key: "today", label: "Hoje" },
  { key: "this_week", label: "Esta semana" },
  { key: "next_week", label: "Próxima semana" },
  { key: "next_7", label: "Próx. 7 dias" },
  { key: "next_15", label: "Próx. 15 dias" },
  { key: "next_30", label: "Próx. 30 dias" },
  { key: "this_month", label: "Este mês" },
  { key: "next_month", label: "Próximo mês" },
  { key: "custom", label: "Personalizado" },
];

const GROUP_OPTIONS = [
  { key: "day", label: "Dia" },
  { key: "week", label: "Semana" },
  { key: "responsible", label: "Responsável" },
  { key: "location", label: "Local" },
  { key: "wbs", label: "EDT (1º nível)" },
  { key: "phase", label: "Fase" },
];

const DEFAULT_COLUMNS = [
  { key: "task_id", label: "ID", type: "string", visible: true, width: 60 },
  { key: "name", label: "Tarefa", type: "string", visible: true, width: 320 },
  { key: "start", label: "Início", type: "date", visible: true, width: 90 },
  { key: "finish", label: "Término", type: "date", visible: true, width: 90 },
  { key: "duration_days", label: "Dur (d)", type: "number", visible: true, width: 70 },
  { key: "percent_complete", label: "%", type: "percent", visible: true, width: 60 },
  { key: "resource_names", label: "Responsável", type: "string", visible: true, width: 160 },
  { key: "location", label: "Local", type: "string", visible: false, width: 120 },
  { key: "deadline", label: "Prazo", type: "date", visible: false, width: 100 },
];

function computePeriod(preset) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startOfWeek = (d) => { const n = new Date(d); const day = (n.getDay() + 6) % 7; n.setDate(n.getDate() - day); return n; };
  const endOfWeek = (d) => { const n = startOfWeek(d); n.setDate(n.getDate() + 6); return n; };
  const fmt = (d) => d.toISOString().slice(0, 10);
  const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
  switch (preset) {
    case "today": return { start: fmt(today), end: fmt(today) };
    case "this_week": return { start: fmt(startOfWeek(today)), end: fmt(endOfWeek(today)) };
    case "next_week": {
      const nws = startOfWeek(addDays(today, 7));
      return { start: fmt(nws), end: fmt(endOfWeek(nws)) };
    }
    case "next_7": return { start: fmt(today), end: fmt(addDays(today, 7)) };
    case "next_15": return { start: fmt(today), end: fmt(addDays(today, 15)) };
    case "next_30": return { start: fmt(today), end: fmt(addDays(today, 30)) };
    case "this_month": {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      const e = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { start: fmt(s), end: fmt(e) };
    }
    case "next_month": {
      const s = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const e = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      return { start: fmt(s), end: fmt(e) };
    }
    default: return null;
  }
}

function getValue(t, key) {
  if (key.startsWith("custom_fields.")) return (t.custom_fields || {})[key.slice("custom_fields.".length)];
  return t[key];
}

function formatCell(v, type) {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (type === "date") return String(v).slice(0, 10);
  if (type === "percent") return `${Math.round(v)}%`;
  if (type === "number") return typeof v === "number" ? v.toLocaleString() : String(v);
  return String(v);
}

function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

export default function WorkProgram() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_super_admin;
  const containerRef = useRef(null);

  const [selection, setSelection] = useState([]);
  const [preset, setPreset] = useState("next_7");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [groupBy, setGroupBy] = useState("day");
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [search, setSearch] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const [multiOpen, setMultiOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [viewsOpen, setViewsOpen] = useState(false);

  const period = useMemo(() => preset === "custom" ? customRange : computePeriod(preset), [preset, customRange]);

  useEffect(() => {
    if (!selection.length || !period?.start || !period?.end) { setGroups([]); return; }
    (async () => {
      setLoading(true);
      try {
        const r = await http.post("/work-program/query", {
          selection, period_start: period.start, period_end: period.end, group_by: groupBy, search: search || undefined,
        });
        setGroups(r.data.groups || []);
      } catch (e) { toast.error(e.response?.data?.detail || e.message); }
      finally { setLoading(false); }
    })();
  }, [selection, period?.start, period?.end, groupBy, search]);

  // Apply per-column filters client-side
  const filteredGroups = useMemo(() => {
    const activeFilters = columns.filter((c) => c.visible && (c.filter || "").trim());
    if (!activeFilters.length) return groups;
    return groups.map((g) => ({
      ...g,
      tasks: g.tasks.filter((t) => activeFilters.every((c) => {
        const v = getValue(t, c.key);
        if (v == null) return false;
        return String(v).toLowerCase().includes(c.filter.trim().toLowerCase());
      })),
    })).filter((g) => g.tasks.length);
  }, [groups, columns]);

  const wiredColumns = useMemo(() => columns.map((c) => ({
    ...c,
    onFilterChange: (key, val) => setColumns((prev) => prev.map((x) => x.key === key ? { ...x, filter: val } : x)),
  })), [columns]);

  const visibleCols = wiredColumns.filter((c) => c.visible);

  const allTasks = useMemo(() => filteredGroups.flatMap((g) => g.tasks), [filteredGroups]);

  const exportCsv = () => {
    const rows = allTasks.map((t) => {
      const o = { Agrupamento: t.__bucket };
      columns.filter((c) => c.visible).forEach((c) => { o[c.label] = getValue(t, c.key) ?? ""; });
      return o;
    });
    const csv = Papa.unparse(rows, { delimiter: ";" });
    downloadBlob(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }), `programacao-${new Date().toISOString().slice(0,10)}.csv`);
  };

  const exportPng = async () => {
    if (!containerRef.current) return;
    try {
      const canvas = await html2canvas(containerRef.current, { backgroundColor: "#FAFAFA", scale: 2, useCORS: true, logging: false });
      canvas.toBlob((b) => b && downloadBlob(b, `programacao-${new Date().toISOString().slice(0,10)}.png`));
    } catch (e) { toast.error(e.message); }
  };

  const exportPdf = async () => {
    // Render the current section as a PNG, then open it in a new tab and let the
    // browser print it (user can "Save as PDF"). We deliberately avoid
    // `document.write` (CWE-79 / Trusted Types) — even though `dataUrl` is a
    // restricted base64 alphabet, building the document via the DOM API is the
    // safer pattern and keeps the linter happy.
    if (!containerRef.current) return;
    try {
      const canvas = await html2canvas(containerRef.current, { backgroundColor: "#FAFAFA", scale: 2, useCORS: true });
      const dataUrl = canvas.toDataURL("image/png");
      const w = window.open("");
      if (!w) return;
      const doc = w.document;
      doc.title = "Programação";
      doc.body.style.margin = "0";
      const img = doc.createElement("img");
      img.src = dataUrl;
      img.style.width = "100%";
      doc.body.appendChild(img);
      img.onload = () => w.print();
    } catch (e) { toast.error(e.message); }
  };

  const currentState = { selection, preset, customRange, groupBy, columns: columns.map((c) => ({ ...c, onFilterChange: undefined })) };
  const applySavedView = (s) => {
    if (s.selection) setSelection(s.selection);
    if (s.preset) setPreset(s.preset);
    if (s.customRange) setCustomRange(s.customRange);
    if (s.groupBy) setGroupBy(s.groupBy);
    if (s.columns) setColumns(s.columns);
  };

  return (
    <div className="px-8 lg:px-12 py-8 max-w-7xl" data-testid="work-program-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="cs-label">programação</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Programação de Trabalho</h1>
          <p className="text-sm text-zinc-600 mt-1">Tarefas ativas no período selecionado, agrupadas conforme sua preferência.</p>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white border border-zinc-200 rounded-md p-3 flex flex-wrap items-center gap-2 mb-3" data-testid="wp-toolbar">
        <button onClick={() => setMultiOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-zinc-200 rounded-md hover:bg-zinc-50" data-testid="wp-multi-btn">
          <Layers size={12} /> {selection.length} projeto{selection.length !== 1 ? "s" : ""}
        </button>
        <select value={preset} onChange={(e) => setPreset(e.target.value)} className="px-3 py-1.5 text-xs border border-zinc-200 rounded-md" data-testid="wp-preset">
          {PERIOD_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        {preset === "custom" && (
          <>
            <input type="date" value={customRange.start} onChange={(e) => setCustomRange({ ...customRange, start: e.target.value })} className="px-2 py-1.5 text-xs border border-zinc-200 rounded font-mono" data-testid="wp-start" />
            <input type="date" value={customRange.end} onChange={(e) => setCustomRange({ ...customRange, end: e.target.value })} className="px-2 py-1.5 text-xs border border-zinc-200 rounded font-mono" data-testid="wp-end" />
          </>
        )}
        <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="px-3 py-1.5 text-xs border border-zinc-200 rounded-md" data-testid="wp-group">
          {GROUP_OPTIONS.map((g) => <option key={g.key} value={g.key}>Agrupar por: {g.label}</option>)}
        </select>
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar…" className="px-3 py-1.5 text-xs border border-zinc-200 rounded-md flex-1 min-w-[160px]" data-testid="wp-search" />
        <button onClick={() => setColumnsOpen(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-zinc-200 rounded-md hover:bg-zinc-50" data-testid="wp-columns-btn">
          <Sliders size={12} /> Colunas
        </button>
        <button onClick={() => setViewsOpen(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-zinc-200 rounded-md hover:bg-zinc-50" data-testid="wp-views-btn">
          <Save size={12} /> Visualizações
        </button>
        <div className="inline-flex items-center bg-zinc-100 rounded-md overflow-hidden">
          <button onClick={exportCsv} className="px-2.5 py-1.5 text-xs hover:bg-zinc-200" data-testid="wp-csv">CSV</button>
          <button onClick={exportPdf} className="px-2.5 py-1.5 text-xs hover:bg-zinc-200" data-testid="wp-pdf">PDF</button>
          <button onClick={exportPng} className="px-2.5 py-1.5 text-xs hover:bg-zinc-200" data-testid="wp-png">PNG</button>
        </div>
      </div>

      <div ref={containerRef} className="space-y-4">
        {!selection.length ? (
          <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center bg-white" data-testid="wp-empty-projects">
            <Layers size={28} className="mx-auto text-zinc-300 mb-2" />
            <p className="text-sm text-zinc-500">Selecione um ou mais projetos para começar.</p>
            <button onClick={() => setMultiOpen(true)} className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-cs-accent hover:underline">
              Selecionar Projetos →
            </button>
          </div>
        ) : loading ? (
          <div className="text-sm text-zinc-500 p-6">Carregando…</div>
        ) : !filteredGroups.length ? (
          <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center bg-white" data-testid="wp-empty-tasks">
            <p className="text-sm text-zinc-500">Nenhuma tarefa ativa no período selecionado.</p>
          </div>
        ) : (
          filteredGroups.map((g) => (
            <div key={g.bucket} className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid={`wp-group-${g.bucket}`}>
              <div className="px-4 py-2.5 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                <h3 className="font-heading font-semibold text-zinc-900 text-sm">{g.bucket}</h3>
                <span className="font-mono text-xs text-zinc-500">{g.tasks.length}</span>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-zinc-50/40 border-b border-zinc-100">
                  <tr>
                    {visibleCols.map((c) => (
                      <th key={c.key} className="cs-label !text-[10px] px-3 py-2 text-left whitespace-nowrap" style={{ width: c.width }}>
                        <div>{c.label}</div>
                        <input type="text" value={c.filter || ""} onChange={(e) => c.onFilterChange?.(c.key, e.target.value)} className="w-full px-1.5 py-0.5 mt-1 text-[10px] border border-zinc-200 rounded font-mono bg-white" placeholder="filtrar…" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {g.tasks.map((task) => (
                    <tr key={task.id} className="border-t border-zinc-50 hover:bg-zinc-50/60 cursor-pointer" onDoubleClick={() => setDetailTask(task)} data-testid={`wp-row-${task.id}`}>
                      {visibleCols.map((c) => (
                        <td key={c.key} className={`px-3 py-1.5 ${c.type === "number" || c.type === "percent" ? "text-right font-mono" : ""}`}>
                          {formatCell(getValue(task, c.key), c.type)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>

      <MultiProjectSelector open={multiOpen} onClose={() => setMultiOpen(false)} value={selection} onChange={setSelection} />
      <ColumnsPanel open={columnsOpen} onClose={() => setColumnsOpen(false)} columns={columns} onChange={setColumns} />
      <SavedViewsMenu open={viewsOpen} onClose={() => setViewsOpen(false)} screen="work_program" currentState={currentState} onApply={applySavedView} canSaveCompany={isAdmin} />
      <TaskDetailModal task={detailTask} onClose={() => setDetailTask(null)} />
    </div>
  );
}
